import { FormEventHandler, MutableRefObject, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Destination,
  ProductionDivision,
  ProductionStatus,
  TaskSearchConditions,
  TreeCompany,
} from '../../../api/model';
import { CheckBox } from '../../atoms/CheckBox';
import MultiSelect from '../../atoms/MultiSelect';
import OrganizationTreeMultiSelect, { getOptions } from '../../modules/OrganizationTreeMultiSelect';
import SlideToggle from '../../modules/SlideToggle';
import MultiSelectStringArray from '../schedulelist/MultiSelectStringArray';

interface Props {
  openSearch: boolean;
  defaultConditions: TaskSearchConditions;
  modelNames: string[];
  destinations: Destination[];
  productionStatuses: ProductionStatus[];
  productionDevisions: ProductionDivision[];
  treeCompanies: TreeCompany[];
  displayGroup: boolean;
  conditionsRef: MutableRefObject<TaskSearchConditions>;
  setConditionsAndSearch: (value: TaskSearchConditions) => void;
}

function TaskSearch({
  openSearch,
  defaultConditions,
  modelNames,
  destinations,
  productionStatuses,
  productionDevisions,
  treeCompanies,
  displayGroup,
  conditionsRef,
  setConditionsAndSearch,
}: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // フォームのデフォルト値
  const defaultValues = useMemo(() => {
    const modelUserIds = defaultConditions.model_user_ids?.map((x) => `user_${x}`);
    return { ...defaultConditions, model_user_ids: modelUserIds, include_completed: true };
  }, [defaultConditions]);

  // フォーム情報
  const { control, register, handleSubmit } = useForm({ defaultValues });

  /*
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(!open);
  };

  const options = useDefaultOptions(language);
  /*
  const destinations = useGetDestinations(options);
  const modelnames = useGetModelNames(options);
  */
  //  const organizationsReseponse = useGetModelUsers(options);
  //  const companies = organizationsReseponse.data?.data.companies || [];
  /*
  const mastersResponse = useGetProductionMasters(options);
  const productionDevisions = mastersResponse.data?.data.production_divisions;
  const pproductionStatuses = mastersResponse.data?.data.production_statuses;
  */

  //  const mastersData = useGetTasksMasters(options);

  //  const [params, setParams] = useSearchParams();
  /*
  const { control, register, handleSubmit } = useForm<TaskSearchConditions>({
    defaultValues: {
      sales_my: params.get('sales_my') || '',
      model_names:
        params
          .get('model_names')
          ?.split(',')
          .map((id) => id) || undefined,
      destination_ids:
        params
          .get('destination_ids')
          ?.split(',')
          .map((did) => Number(did)) || undefined,
      model_user_ids:
        params
          .get('model_user_ids')
          ?.split(',')
          .map((did) => Number(did)) || undefined,
      production_status_ids:
        params
          .get('production_status_ids')
          ?.split(',')
          .map((id) => Number(id)) || undefined,
      production_no: params.get('production_no') || '',
      production_division_ids:
        params
          .get('production_division_ids')
          ?.split(',')
          .map((id) => Number(id)) || undefined,
      free_word: params.get('free_word') || '',
      include_completed: params.get('include_completed') === '1' ? true : false || true, // デフォルトでチェックON
      deleted: params.get('include_completed') === '1' ? true : false || undefined,
    },
  });

  type ParamsProps = {
    display_group?: string;
    sales_my?: string;
    model_names?: string;
    destination_ids?: string;
    production_status_ids?: string;
    model_user_ids?: string;
    production_no?: string;
    production_division_ids?: string;
    free_word?: string;
    include_completed?: string;
    deleted?: string;
  };
  const onSubmit: SubmitHandler<TaskSearchConditions> = (data) => {
    const obj: ParamsProps = {};
    obj.display_group = params.get('display_group') || '0';
    if (data.sales_my) {
      obj.sales_my = data.sales_my;
    }
    if (data.model_names !== undefined && data.model_names.length > 0) {
      obj.model_names = String(data.model_names);
    }
    if (data.destination_ids && data.destination_ids.length > 0) {
      obj.destination_ids = String(data.destination_ids);
    }
    if (data.production_status_ids && data.production_status_ids.length > 0) {
      obj.production_status_ids = String(data.production_status_ids);
    }
    if (data.model_user_ids && data.model_user_ids.length > 0) {
      obj.model_user_ids = String(data.model_user_ids);
    }
    if (data.production_no) {
      obj.production_no = String(data.production_no);
    }
    if (data.production_division_ids && data.production_division_ids.length > 0) {
      obj.production_division_ids = String(data.production_division_ids);
    }
    if (data.free_word) {
      obj.free_word = data.free_word;
    }
    obj.include_completed = String(data.include_completed ? 1 : 0);
    obj.deleted = String(data.deleted ? 1 : 0);

    setParams(obj);
  };
  */

  // フォーム送信
  const onSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    void handleSubmit((data) => {
      // 組織コンポネントを使っているため、user_{number} の形でデータが送られるので number に変換する
      const modelUserIds =
        data.model_user_ids != null
          ? data.model_user_ids.flatMap((x) => {
              const splited = x.split('_');
              return splited.length === 2 ? parseInt(splited[1], 10) : [];
            })
          : undefined;
      const next: TaskSearchConditions = {
        ...conditionsRef.current,
        sales_my: data.sales_my,
        model_names: data.model_names,
        destination_ids: data.destination_ids,
        production_status_ids: data.production_status_ids,
        model_user_ids: modelUserIds,
        production_no: data.production_no,
        production_division_ids: data.production_division_ids,
        free_word: data.free_word,
        include_completed: data.include_completed,
        deleted: data.deleted,
        display_group: displayGroup,
      };

      // 検索実行など
      setConditionsAndSearch(next);
    })();
  };

  return (
    <>
      <SlideToggle open={openSearch}>
        <form onSubmit={onSubmit}>
          <div className="wfr scondition trigfr fropen">
            <div className="grid3 labip">
              <div>
                <label htmlFor="ip4-1">{t('M/Y')}</label>
                <input type="text" {...register('sales_my')} />
              </div>
              <div>
                <label htmlFor="production_status_ids">{t('ステータス')}</label>
                <MultiSelect
                  id="production_status_ids"
                  name="production_status_ids"
                  options={productionStatuses}
                  control={control}
                  isDisabled={false}
                  getValue={(x) => x.production_status_id}
                  getLabel={(x) => x.production_status_name}
                />
              </div>
              <div>
                <label htmlFor="production_division_ids">{t('資料区分')}</label>
                <MultiSelect
                  id="production_division_ids"
                  name="production_division_ids"
                  options={productionDevisions}
                  control={control}
                  isDisabled={false}
                  getValue={(x) => x.production_division_id}
                  getLabel={(x) => x.production_division_name}
                />
              </div>
              <div>
                <label htmlFor="model_name">{t('機種名')}</label>
                <MultiSelectStringArray
                  id="model_names"
                  name="model_names"
                  options={modelNames}
                  control={control}
                  isDisabled={false}
                  getValue={(x) => x}
                  getLabel={(x) => x}
                />
              </div>
              <div>
                <label htmlFor="ip4-4">{t('機種担当')}</label>
                <span className="dropdown">
                  <OrganizationTreeMultiSelect
                    id="model_user_ids"
                    name="model_user_ids"
                    control={control}
                    options={getOptions(treeCompanies, language === 'ja')}
                    selectableTypes={['user']}
                    isDisabled={false}
                  />
                </span>
              </div>
              <div>
                <label htmlFor="ip4-5">{t('フリーワード')}</label>
                <input type="text" {...register('free_word')} />
              </div>
              <div>
                <label htmlFor="destination_ids">{t('仕向')}</label>
                <MultiSelect
                  id="destination_ids"
                  name="destination_ids"
                  options={destinations}
                  control={control}
                  isDisabled={false}
                  getValue={(x) => x.destination_id}
                  getLabel={(x) => x.destination_name}
                />
              </div>
              <div>
                <label htmlFor="ip4-7">{t('資料管理No')}</label>
                <input type="text" {...register('production_no')} />
              </div>
            </div>
            <div className="controlfr" style={{ marginTop: '15px' }}>
              <span className="ck">
                <CheckBox
                  name="include_completed"
                  labelBefore={t('完成済み資料を含める').toString()}
                  register={register}
                />
              </span>
              <span className="ck ml10">
                <CheckBox name="deleted" labelBefore={t('削除データ表示').toString()} register={register} />
              </span>
              <span>
                <input id="search" name="search" type="submit" value={t('検索').toString()} className="searchbtn" />
              </span>
            </div>
          </div>
        </form>
      </SlideToggle>
      <div className="contfr mt20" />
    </>
  );
}

export { TaskSearch };
